import { Component, inject, OnInit } from '@angular/core';
import { AppInformationService, BrewersUnitOfWork } from 'app/core/core.module';
import { Role } from 'app/core/model/role';
import { User } from 'app/core/model/user';
import { UserService } from 'app/core/user.service';
import { alert } from 'devextreme/ui/dialog';
import validationEngine from 'devextreme/ui/validation_engine';
import _ from 'lodash';
import { Subject } from 'rxjs';

@Component({
    standalone: false,
    selector: 'app-user-dialog',
    template: `
        <div class="modal-header">
            <h4 class="modal-title">{{ isNew ? 'Add' : 'Edit' }} User</h4>
        </div>
        <div class="modal-body">
            <dx-form [formData]="user" validationGroup="userValidationGroup">
                <dxi-item dataField="firstName"> </dxi-item>
                <dxi-item dataField="lastName"> </dxi-item>
                <dxi-item dataField="userName" [isRequired]="true"> </dxi-item>
                <dxi-item dataField="email"> </dxi-item>
                <dxi-item dataField="active" editorType="dxCheckBox"> </dxi-item>
                <dxi-item dataField="newPassword" [editorOptions]="{ mode: 'password', name: 'new-password', onValueChanged: onValueChanged, valueChangeEvent: 'keyup' }">
                    <dxi-validation-rule
                        type="custom"
                        message="Password cannot contain any spaces"
                        [validationCallback]="validatePasswordSpaces"
                        [reevaluate]="true"
                    ></dxi-validation-rule>
                    <dxi-validation-rule
                        type="custom"
                        message="Password must be at least 8 characters in length"
                        [validationCallback]="validatePassword"
                        [reevaluate]="true"
                    ></dxi-validation-rule>
                    <dxi-validation-rule
                        type="custom"
                        message="Password required for new users"
                        [validationCallback]="validatePasswordRequired"
                        [reevaluate]="true"
                    ></dxi-validation-rule>
                </dxi-item>
                <dxi-item [label]="{ text: 'Roles' }">
                    <div *dxTemplate>
                        <dx-list
                            [items]="roles"
                            keyExpr="id"
                            [(selectedItemKeys)]="selectedRoleIds"
                            selectionMode="multiple"
                            [showSelectionControls]="true"
                            itemTemplate="listItem"
                        >
                            <div *dxTemplate="let item of 'listItem'">
                                {{ item.name }}
                            </div>
                        </dx-list>
                    </div>
                </dxi-item>
            </dx-form>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-success" (click)="save()">Save</button>
            <button type="button" class="btn btn-light" (click)="cancel()">Cancel</button>
        </div>
    `,
    styles: ``,
})
export class UserDialogComponent implements OnInit {
    uow = inject(BrewersUnitOfWork);
    userService = inject(UserService);
    appInfo = inject(AppInformationService);

    result = new Subject<{ success: boolean; value?: User }>();
    user: User;
    roles: Role[];
    selectedRoleIds: number[];
    currentRoleIds: number[];
    isNew: boolean;

    ngOnInit() {
        this.roles = this.uow.lookups.roles;

        if (!this.user) {
            this.isNew = true;
            this.user = this.uow.users.createEntity() as User;
        }

        this.currentRoleIds = this.user?.userRoles.map((userRoles) => userRoles.roleId) ?? [];
        this.selectedRoleIds = this.currentRoleIds.slice();
    }

    hasPasswordChanged = false as boolean;
    onValueChanged = (e: any) => {
        this.hasPasswordChanged = e.value && e.value.trim() !== '' ? true : false;
    };

    async save() {
        const success = this.valid();
        if (!success) return;

        const addedRoleIds = _.difference(this.selectedRoleIds, this.currentRoleIds);
        const removedRoleIds = _.difference(this.currentRoleIds, this.selectedRoleIds);

        addedRoleIds.forEach((roleId) => {
            this.uow.userRoles.createEntity({
                roleId: roleId,
                userId: this.user.id,
            });
        });

        this.user.userRoles
            .filter((userRole) => removedRoleIds.includes(userRole.roleId))
            .forEach((userRole) => {
                userRole.entityAspect.setDeleted();
            });
        await this.uow.commit();

        if (this.user.newPassword && this.user.newPassword.trim() !== '') {
            const response = await this.userService.changePassword(this.user.id, this.user.newPassword.trim());
            if (!response.success) await alert(`Error changing password. ${response.message}`, this.appInfo.settings.company);

            this.hasPasswordChanged = false;
            this.user.newPassword = null;
        }

        this.result.next({ success: true, value: this.user });
    }

    cancel() {
        this.uow.rollback();

        this.result.next({ success: false });
    }

    valid() {
        const groupConfig = validationEngine.getGroupConfig('userValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('userValidationGroup');
            return result.isValid;
        } else return false;
    }

    validatePasswordSpaces = (e: any) => {
        if (!e.value) return true;
        else return e.value.replaceAll(' ') === e.value;
    };

    validatePassword = (e: any) => {
        if (!e.value || e.value.trim() === '') return true;
        else return e.value.trim().length >= 8;
    };

    validatePasswordRequired = (e: any) => {
        return this.user.id > 0 || (!!e.value && e.value.trim() !== '');
    };
}
